import React, {useEffect, useRef, useState} from 'react'

import styled, {css, ThemeProvider} from 'styled-components'
import theme from './theme'
import HeaderLogo from './components/HeaderLogo'
import {Box, checkUserInfo, Hide, Link, LanguageSwitcher, useWindowSize} from '@miro-site/core'

import {IHeader} from './api/interfaces'

import ContactSalesLink from './components/ContactSalesLink'
import TopNavigation from './components/TopNavigation'
import CtaNavigation from './components/CtaNavigation'
import BurgerMenu from './components/BurgerMenu'
import AccordionMenu from './components/AccordionMenu'
import HeaderContainer from './components/HeaderContainer'
import SecondaryNavigationContainer from './components/SecondaryNavigationContainer'
import NavigationOverlay from './components/NavigationOverlay'
import {usePageTranslations} from '@miro-site/localization'
import {useLocalization} from '@miro-site/localization'
import {HeaderAppearance} from '../types/Header'
import {isDarkTheme} from './utils'

const StyledHeader = styled(Box).attrs({
  as: 'header',
  $styles: {
    fontFamily: 'Formular',
    fontFeatureSettings: "'ss03'",
  },
})`
  *,
  *:before,
  *:after {
    box-sizing: border-box;
  }
`

StyledHeader.displayName = 'StyledHeader'

const HeaderRoot = styled(Box).attrs<{
  $appearance: HeaderAppearance
  $isMobileMenuOpened?: boolean
}>(({$isMobileMenuOpened, $appearance, theme: {header}}) => ({
  $styles: {
    zIndex: 10,
    position: 'relative',
    background:
      $isMobileMenuOpened || isDarkTheme($appearance)
        ? theme.header.backgroundDark
        : theme.header.background,
    borderBottom: isDarkTheme($appearance) ? 'none' : header.secondaryNavigation.stroke,
    color: isDarkTheme($appearance) ? theme.header.colors.white : theme.header.colors.indigo,
  },
}))<{$appearance: HeaderAppearance; $isMobileMenuOpened?: boolean}>`
  transition: background 250ms ease;
`

const HeaderNav = styled(Box).attrs(({theme}) => ({
  as: 'nav',
  $styles: {
    height: theme.header.height,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexFlow: 'row nowrap',
    paddingRight: theme.header.navigation.padding.right,
  },
}))`
  ${LanguageSwitcher.SwitcherCurrentLocale} {
    ${({theme}) =>
      theme.normalizeStyles({
        fontSize: theme.header.topNavigation.fontSize,
        lineHeight: theme.header.topNavigation.lineHeight,
        display: ['block', 'block', 'none', 'block', 'block'],
      })}
  }
`

const AccordionMobileContainer = styled(Box).attrs<{$isMobileMenuOpened?: boolean}>(
  ({theme, $isMobileMenuOpened}) => ({
    $styles: {
      background: 'primaryIndigo',
      overflowY: 'auto',
      position: 'fixed',
      top: theme.header.height,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0,
      visibility: 'hidden',
      transition: 'opacity 250ms ease, visibility 250ms ease',
    },
  }),
)<{$isMobileMenuOpened?: boolean}>`
  ${({$isMobileMenuOpened}) =>
    $isMobileMenuOpened &&
    css`
      opacity: 1;
      visibility: visible;
    `}
`

const MobileControls = styled(Box).attrs({
  $styles: {
    textAlign: 'center',
    paddingTop: 64,
    paddingBottom: 36,
  },
})``

const MobileLinksContainer = styled(Box).attrs({
  $styles: {
    marginTop: 23,
    marginBottom: 20,
  },
})``

const MobileLink = styled(Link).attrs({
  $mx: 10,
})``

const LanguageSwitcherContainer = styled(Box).attrs({
  $styles: {
    display: 'flex',
    justifyContent: 'center',
  },
})``

const LoginLink = styled(Link).attrs<{$appearance: HeaderAppearance}>(({$appearance, theme}) => ({
  $styles: {
    fontSize: theme.header.topNavigation.fontSize,
    lineHeight: theme.header.topNavigation.lineHeight,
    display: 'block',
    color: 'inherit',
    ':hover': {
      color: isDarkTheme($appearance)
        ? theme.colors.outspoken
        : theme.header.topNavigation.hoverColor,
    },
  },
}))<{$appearance: HeaderAppearance}>`
  ${({theme}) =>
    theme.normalizeStyles({
      ':focus': {
        ...theme.focus.outlines.withOffset,
      },
    })}
`

interface HeaderProps extends IHeader {
  containerNode?: HTMLElement
  appearance?: HeaderAppearance
  className?: string
}

const Header: React.FC<HeaderProps> = props => {
  const {
    label,
    productNavigation,
    solutionsNavigation,
    resourcesNavigation,
    enterpriseLink,
    pricingLink,
    contactSalesLink,
    loginLink,
    dashboardButton,
    signupButton,
    bottomLinks,
    customerSpotlight,
    templates,
    whatsNew,
    containerNode,
    appearance = 'light',
    className,
  } = props

  const mobileMenuData = [
    productNavigation,
    solutionsNavigation,
    resourcesNavigation,
    enterpriseLink,
    pricingLink,
  ]

  const [userId, setUserId] = useState<number>()
  const [isMobileMenuOpened, setIsMobileMenuOpened] = useState(false)
  const [activeNavigation, setActiveNavigation] = useState<number | null>(null)
  const [switcherData, {onChangeLocale}] = usePageTranslations()
  const {width} = useWindowSize()
  const {currentLocale} = useLocalization()
  const isRUlocale = currentLocale == 'ru' ? true : false
  const isMobileScreen = typeof width === 'number' && width <= theme.breakpoints.v2Tablet
  const activeNavigationRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const {id: userId = 0} = checkUserInfo() || {}

    setUserId(userId)
  }, [])

  useEffect(() => {
    if (containerNode) {
      if (isMobileMenuOpened) {
        containerNode.classList.add('mobile-menu-opened')
      } else {
        containerNode.classList.remove('mobile-menu-opened')
      }
    }

    document.body.style.overflow = isMobileMenuOpened ? 'hidden' : ''
  }, [isMobileMenuOpened])

  const toggleNavigation = (navigation: number) => {
    setActiveNavigation(current => (current === navigation ? null : navigation))
  }

  return (
    <ThemeProvider theme={defaultTheme => ({...defaultTheme, ...theme})}>
      <StyledHeader data-place="Header" className={className}>
        <HeaderRoot $appearance={appearance} $isMobileMenuOpened={isMobileMenuOpened}>
          <HeaderContainer>
            <HeaderNav aria-label={label} data-track-place="Header">
              <HeaderLogo
                href="/"
                color={isMobileMenuOpened || isDarkTheme(appearance) ? 'white' : 'primaryIndigo'}
              />

              {!isMobileMenuOpened && (
                <TopNavigation
                  onToggleNavigation={toggleNavigation}
                  activeNavigationRef={activeNavigationRef}
                  activeNavigation={activeNavigation}
                  productNavigation={productNavigation}
                  solutionsNavigation={solutionsNavigation}
                  resourcesNavigation={resourcesNavigation}
                  templates={templates}
                  customerSpotlight={customerSpotlight}
                  whatsNew={whatsNew}
                  enterpriseLink={enterpriseLink}
                  pricingLink={pricingLink}
                  appearance={appearance}
                />
              )}

              {!isMobileMenuOpened && (
                <CtaNavigation>
                  <Hide $on={['v2Mobile']}>
                    {switcherData && (
                      <LanguageSwitcher
                        color={isDarkTheme(appearance) ? 'white' : undefined}
                        onSelect={(e, link) => {
                          onChangeLocale(link.locale)
                        }}
                        {...switcherData}
                      />
                    )}
                  </Hide>
                  <Hide $on={['v2Mobile']}>
                    {isRUlocale ? null : (
                      <ContactSalesLink
                        href={contactSalesLink.path}
                        label={contactSalesLink.title}
                        appearance={appearance}
                      />
                    )}
                  </Hide>
                  {userId !== undefined && userId === 0 && (
                    <Hide $on={['v2Mobile']}>
                      <LoginLink
                        href={loginLink.path}
                        $color="default"
                        $size="default"
                        $appearance={appearance}
                      >
                        {loginLink.title}
                      </LoginLink>
                    </Hide>
                  )}
                  {userId !== undefined &&
                    (userId > 0 ? (
                      <CtaNavigation.Button href={dashboardButton.path}>
                        {dashboardButton.title}
                      </CtaNavigation.Button>
                    ) : (
                      <CtaNavigation.Button href={signupButton.path} needReferer>
                        {signupButton.title}
                      </CtaNavigation.Button>
                    ))}
                </CtaNavigation>
              )}

              {(isMobileMenuOpened || isMobileScreen) && (
                <BurgerMenu
                  appearance={appearance}
                  onClick={() => {
                    setIsMobileMenuOpened(!isMobileMenuOpened)
                  }}
                  $isMobileMenuOpened={isMobileMenuOpened}
                />
              )}
            </HeaderNav>

            <AccordionMobileContainer $isMobileMenuOpened={isMobileMenuOpened}>
              <AccordionMenu categories={mobileMenuData} />

              {isMobileScreen && (
                <MobileControls>
                  {userId !== undefined &&
                    (userId > 0 ? (
                      <CtaNavigation.Button href={dashboardButton.path} $isMobileButton={true}>
                        {dashboardButton.title}
                      </CtaNavigation.Button>
                    ) : (
                      <>
                        <CtaNavigation.Button
                          href={signupButton.path}
                          needReferer
                          $isMobileButton={true}
                        >
                          {signupButton.title}
                        </CtaNavigation.Button>
                      </>
                    ))}

                  <MobileLinksContainer>
                    <MobileLink href={loginLink.path} $color="white" $size="default">
                      {loginLink.title}
                    </MobileLink>
                    {isRUlocale ? null : (
                      <MobileLink href={contactSalesLink.path} $color="white" $size="default">
                        {contactSalesLink.title}
                      </MobileLink>
                    )}
                  </MobileLinksContainer>

                  <LanguageSwitcherContainer>
                    {switcherData && (
                      <LanguageSwitcher
                        {...switcherData}
                        onSelect={(e, link) => {
                          onChangeLocale(link.locale)
                        }}
                        color="white"
                        position="top"
                      />
                    )}
                  </LanguageSwitcherContainer>
                </MobileControls>
              )}
            </AccordionMobileContainer>
          </HeaderContainer>
        </HeaderRoot>
        {!isMobileScreen && (
          <>
            <SecondaryNavigationContainer
              isFixed={false}
              activeNavigation={activeNavigation}
              activeNavigationRef={activeNavigationRef}
              bottomLinks={bottomLinks}
            />
            <NavigationOverlay
              onClick={() => setActiveNavigation(null)}
              $isActive={activeNavigation !== null}
            />
          </>
        )}
      </StyledHeader>
    </ThemeProvider>
  )
}

export default Header
